import { toast } from '@bozic/ui/toast';
import axios from 'axios';

import { bodyMessage } from '@/shared/utils/map-error-message';
import userAgreementNotAcceptedError from '@/shared/utils/userAgreementNotAcceptedError';

export const errorResponseStatusHandler = (
  response: unknown,
  customMessageConfig?: { status: number; message: string },
  ignoreHttpStatus?: [number]
  // eslint-disable-next-line complexity -- legacy handler predates the limit; split on the next real touch
) => {
  console.error('Error', response);
  if (!response) return;

  if (userAgreementNotAcceptedError(response)) {
    window.history.pushState({}, '', '/user-agreement-form');
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  if (typeof response === 'string') {
    toast.error(response);
    return;
  }

  let data: unknown;
  let status: number | undefined;
  if (axios.isAxiosError(response)) {
    data = response.response?.data;
    status = response.response?.status;
  } else if (response && typeof response === 'object' && 'data' in response) {
    data = response.data;
    status = (response as { status?: number }).status;
  }
  if (status) {
    if (ignoreHttpStatus?.find(x => x === status)) return;

    raiseNotification(
      { status, data },
      customMessageConfig?.status === status ? customMessageConfig.message : undefined
    );
  }
};

const raiseNotification = (
  { status, data }: { status: number; data: unknown },
  customMessage?: string
  // eslint-disable-next-line complexity -- legacy handler predates the limit; split on the next real touch
) => {
  switch (status) {
    case 400:
    case 409: {
      if (data && typeof data === 'object' && 'Definitions' in data) {
        /** If Custom Error Definitions is provided skip notification */
        return;
      }

      const { errors } = (data ?? {}) as { errors?: Record<string, string[]> };
      if (!errors) {
        toast.error(customMessage ?? extractMessage(data, data));
      }

      for (const errorMessage of Object.values(errors ?? {}).flat()) {
        toast.error(errorMessage);
      }

      break;
    }
    case 500:
      toast.error(extractMessage(data, customMessage ?? 'Error fetching data.'));
      break;
  }
};

const extractMessage = (data: unknown, fallbackValue: unknown): string => {
  // an object body with nothing human-readable falls back — never String() it at users
  const fallback =
    typeof fallbackValue === 'string' ? fallbackValue : 'An unexpected error occurred.';
  return bodyMessage(data) ?? fallback;
};
